import * as React from 'react';
import { Uno, Checkers, YouTube } from '../playables';
import PlayableWrapper from '../playables/PlayableWrapper';
import { spotifyIcon, magicIcon, imageIcon } from '../../assets/icons';
import { unoIcon, checkersIcon, youtubeIcon } from '../../assets/playables/icons';
import { unoThumbnail, checkersThumbnail, youtubeThumbnail } from '../../assets/playables/thumbnails';

// Define types
export interface Playable {
  id: string;
  title: string;
  image: string;
  icon?: string;
  description?: string;
  component: React.ComponentType<any>;
  behaviorType: 'foreground' | 'background';
}

export interface Inputable {
  id: string;
  title: string;
  icon: string;
  inputIcon: string;
  inputPlaceholder?: string;
}

export interface PlayableResult {
  id: string;
  type: 'playable';
  title: string;
  image: string;
  icon?: string;
  description?: string;
  component: React.ComponentType<any>;
  behaviorType: 'foreground' | 'background';
}

export interface InputableResult {
  id: string;
  type: 'inputable';
  title: string;
  icon: string;
  inputIcon: string;
  inputPlaceholder?: string;
}

export const playables: Playable[] = [
  {
    id: 'youtube',
    title: 'YouTube',
    image: youtubeThumbnail,
    icon: youtubeIcon,
    description: 'Watch YouTube with {{char}}',
    component: YouTube,
    behaviorType: 'foreground'
  },
  { 
    id: 'uno', 
    title: 'One', 
    image: unoThumbnail,
    icon: unoIcon,
    component: Uno,
    behaviorType: 'foreground'
  },
  {
    id: 'checkers',
    title: 'Checkers',
    image: checkersThumbnail,
    icon: checkersIcon,
    component: Checkers,
    behaviorType: 'foreground'
  }
];

export const inputables: Inputable[] = [
  {
    id: 'spotify',
    title: 'Search Song',
    icon: spotifyIcon,
    inputIcon: spotifyIcon,
    inputPlaceholder: 'Search for a song...'
  },
  {
    id: 'upload-image',
    title: 'Upload Image',
    icon: imageIcon,
    inputIcon: imageIcon
  },
  {
    id: 'generate-image',
    title: 'Generate Image',
    icon: magicIcon,
    inputIcon: magicIcon,
    inputPlaceholder: 'Generate an image...'
  },
];

// Create lookup maps with proper types
const playablesMap: Record<string, Playable> = playables.reduce((acc: Record<string, Playable>, playable) => {
  acc[playable.id] = playable;
  return acc;
}, {});

// Create lookup map for inputables
const inputablesMap: Record<string, Inputable> = inputables.reduce((acc: Record<string, Inputable>, inputable) => {
  acc[inputable.id] = inputable;
  return acc;
}, {});

export const getPlayableById = (id: string): PlayableResult | null => {
  const playable = playablesMap[id];
  if (!playable) return null;
  
  // Wrap the component in our PlayableWrapper to handle lazy loading
  const wrappedComponent = (props: any) => 
    React.createElement(PlayableWrapper, { 
      ...props, 
      component: playable.component 
    });
  
  return {
    id: id,
    type: 'playable',
    title: playable.title,
    image: playable.image,
    icon: playable.icon,
    description: playable.description,
    component: wrappedComponent, // Use the wrapped component
    behaviorType: playable.behaviorType
  };
};

export const getInputableById = (id: string): InputableResult | null => {
  const inputable = inputablesMap[id];
  if (!inputable) return null;
  
  return {
    id: id,
    type: 'inputable',
    title: inputable.title,
    icon: inputable.icon,
    inputIcon: inputable.inputIcon,
    inputPlaceholder: inputable.inputPlaceholder
  };
};

/**
 * Get all playables that should be displayed in the current environment
 * @returns All playables available for display
 */
export const getFilteredPlayables = (): Playable[] => {
  // We now return all playables regardless of environment
  return playables;
}; 